import type { SparkleLinkProps } from "@dust-tt/sparkle";
import { forwardRef } from "react";
import { Link } from "react-router-dom";

type UrlObjectLike = {
  pathname?: string | null;
  search?: string | null;
  hash?: string | null;
  query?: string | Record<string, unknown> | null;
};

// Formats a Next-style UrlObject with browser APIs only. The previous
// implementation used Node's `url.format`, which the production bundle
// externalises to a stub, so any object href threw "format is not a function"
// at render time. Vite's dev server polyfills the builtin, which is why this
// only ever failed in a built bundle.
function formatHref(href: UrlObjectLike): string {
  const pathname = href.pathname ?? "";

  let search = "";
  if (typeof href.search === "string") {
    search = href.search;
  } else if (typeof href.query === "string") {
    search = href.query;
  } else if (href.query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(href.query)) {
      if (value === undefined || value === null) {
        continue;
      }
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, String(v)));
      } else {
        params.append(key, String(value));
      }
    }
    search = params.toString();
  }
  if (search && !search.startsWith("?")) {
    search = `?${search}`;
  }

  let hash = href.hash ?? "";
  if (hash && !hash.startsWith("#")) {
    hash = `#${hash}`;
  }

  return `${pathname}${search}${hash}`;
}

// Link wrapper that uses React Router's Link for SPA navigation
export const ReactRouterLinkWrapper = forwardRef<
  HTMLAnchorElement,
  SparkleLinkProps
>(function ReactRouterLinkWrapper(
  { href, children, shallow: _shallow, replace, prefetch: _prefetch, ...props },
  ref
) {
  const hrefString =
    typeof href !== "string" ? formatHref(href as UrlObjectLike) : href;

  // For external links or API routes, use regular anchor
  if (
    hrefString.startsWith("http://") ||
    hrefString.startsWith("https://") ||
    hrefString.startsWith("/api/")
  ) {
    return (
      <a ref={ref} href={hrefString} {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link ref={ref} to={hrefString} replace={replace} {...props}>
      {children}
    </Link>
  );
});
